import { APP_BRAND } from '../../core/branding/brand.config';

/**
 * Configuration for the reusable branded error page.
 *
 * Each in-app error route (`**`, `/error/500`, `/error/unavailable`) supplies one
 * of these objects via route `data` so the single {@link ErrorPageComponent} can
 * render the appropriate copy and recovery actions.
 */
export interface ErrorPageAction {
  /** Visible, explicit button/link label (used as the accessible name). */
  readonly label: string;
  /** Optional Material icon name shown alongside the label (decorative). */
  readonly icon?: string;
  /** Internal Angular route to navigate to, e.g. `/dashboard`. */
  readonly routerLink?: string;
  /**
   * Non-navigation behaviour. `refresh` reloads the current page, which is the
   * primary recovery action for the temporarily-unavailable state.
   */
  readonly behavior?: 'refresh';
}

export interface ErrorPageConfig {
  /** Status code shown in the visual panel, e.g. `404`. */
  readonly statusCode: string;
  /** Short eyebrow label above the heading. */
  readonly eyebrow: string;
  /** Single page heading rendered as the `<h1>`. */
  readonly heading: string;
  /** Supportive, non-technical explanation. */
  readonly message: string;
  /** Decorative Material icon name for the status panel. */
  readonly icon: string;
  /** Primary recovery action. */
  readonly primaryAction: ErrorPageAction;
  /** Optional secondary recovery action. */
  readonly secondaryAction?: ErrorPageAction;
}

/** Route `data` key used to pass an {@link ErrorPageConfig} to the component. */
export const ERROR_PAGE_DATA_KEY = 'error' as const;

/** Route `data` shape consumed by {@link ErrorPageComponent}. */
export interface ErrorPageRouteData {
  readonly [ERROR_PAGE_DATA_KEY]?: ErrorPageConfig;
}

const DASHBOARD_ACTION: ErrorPageAction = {
  label: 'Go to dashboard',
  icon: 'home',
  routerLink: '/dashboard',
};

const HELP_ACTION: ErrorPageAction = {
  label: 'Visit help',
  icon: 'help_outline',
  routerLink: '/help',
};

const REFRESH_ACTION: ErrorPageAction = {
  label: 'Refresh page',
  icon: 'refresh',
  behavior: 'refresh',
};

/**
 * 404 — used by the wildcard route and as the component's built-in default when
 * no route data is supplied.
 */
export const NOT_FOUND_ERROR: ErrorPageConfig = {
  statusCode: '404',
  eyebrow: 'Page not found',
  heading: 'We could not find that page',
  message:
    'The link may have changed or the page may have moved. Let us help you get back to where you need to be.',
  icon: 'travel_explore',
  primaryAction: DASHBOARD_ACTION,
  secondaryAction: HELP_ACTION,
};

/** 500 — something went wrong loading part of the app. */
export const SERVER_ERROR: ErrorPageConfig = {
  statusCode: '500',
  eyebrow: 'Unexpected error',
  heading: 'Something needs attention',
  message: `We are having trouble loading that part of ${APP_BRAND.shortProductName}. This is usually temporary, so please head back to your dashboard and try again in a moment.`,
  icon: 'report_problem',
  primaryAction: DASHBOARD_ACTION,
  secondaryAction: HELP_ACTION,
};

/** 503 — the whole service is temporarily unavailable. */
export const SERVICE_UNAVAILABLE_ERROR: ErrorPageConfig = {
  statusCode: '503',
  eyebrow: 'Temporarily unavailable',
  heading: `${APP_BRAND.shortProductName} is temporarily unavailable`,
  message:
    'We are carrying out essential maintenance or are experiencing high demand. Please refresh the page or try again in a little while.',
  icon: 'cloud_off',
  primaryAction: REFRESH_ACTION,
  secondaryAction: HELP_ACTION,
};
